import { useCallback, useEffect, useState } from 'react';
import { AppState, Platform } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import NetInfo from '@react-native-community/netinfo';
import { DEVICE_ISSUE_TYPE, TDeviceIssue } from 'app/types/deviceIssue';
import { useDataFreshness, usePathwayId } from 'app/_modules/pathways';

const BATTERY_LOW_THRESHOLD = 0.1;
const WIFI_WEAK_STRENGTH_THRESHOLD = 30;
const BATTERY_POLL_INTERVAL_MS = 60_000;

export const useDeviceIssues = (): TDeviceIssue[] => {
  const [tabletBatteryLow, setTabletBatteryLow] = useState(false);
  const [routerWeakSignal, setRouterWeakSignal] = useState(false);
  const pathwayId = usePathwayId();
  const { data: dataFreshness, isError: isDataFreshnessError } = useDataFreshness(pathwayId);
  const gatewayDisconnected = !isDataFreshnessError && dataFreshness?.isGatewayConnected === false;

  const checkBatteryLevel = useCallback(async () => {
    const level = await DeviceInfo.getBatteryLevel();
    setTabletBatteryLow(level >= 0 && level < BATTERY_LOW_THRESHOLD);
  }, []);

  useEffect(() => {
    checkBatteryLevel();
    const interval = setInterval(checkBatteryLevel, BATTERY_POLL_INTERVAL_MS);
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') checkBatteryLevel();
    });

    return () => {
      clearInterval(interval);
      subscription.remove();
    };
  }, [checkBatteryLevel]);

  useEffect(() => {
    if (Platform.OS !== 'android') return;

    return NetInfo.addEventListener((state) => {
      const strength = state.type === 'wifi' ? state.details?.strength : undefined;
      setRouterWeakSignal(typeof strength === 'number' && strength < WIFI_WEAK_STRENGTH_THRESHOLD);
    });
  }, []);

  return [
    { issueType: DEVICE_ISSUE_TYPE.gatewayDisconnected, active: gatewayDisconnected },
    { issueType: DEVICE_ISSUE_TYPE.tabletBatteryLow, active: tabletBatteryLow },
    { issueType: DEVICE_ISSUE_TYPE.routerWeakSignal, active: routerWeakSignal },
  ];
};
